/**
 * Copyright (c) 2026 Spicy Friends Ltd.
 * All rights reserved.
 *
 * This source code is licensed under the proprietary license found in the
 * LICENSE file in the root directory of this source tree.
 */

import { gql, type TypedDocumentNode } from '@apollo/client'
import { QueryRef, useReadQuery } from '@apollo/client/react'
import { ErrorAlert, PageHeader, PaginationControl, RouteLoadingIndicator } from '@repo/shared-client/components/blocks'
import { NativeSelectFormField } from '@repo/shared-client/components/fields'
import {
  Button,
  Empty,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@repo/shared-client/components/ui'
import { Form, useForm } from '@repo/shared-client/form'
import { createFileRoute, stripSearchParams, useNavigate, useRouterState, useSearch } from '@tanstack/react-router'
import Footer from '@web/src/features/footer/footer'
import VmCard, { VmCardLayoutMode } from '@web/src/features/vendor-market/vm-card/vm-card'
import {
  type VmListFilterFormValues,
  vmListFilterFormValuesFromResolvedParams,
} from '@web/src/features/vendor-market/vm-list-filter-types'
import VmListSideNav from '@web/src/features/vendor-market/vm-list-side-nav/vm-list-side-nav'
import VmListTopNav from '@web/src/features/vendor-market/vm-list-top-nav/vm-list-top-nav'
import VmMenu from '@web/src/features/vendor-market/vm-menu/vm-menu'
import { useAuth } from '@web/src/hooks/use-auth'
import { queryAndPreload } from '@web/src/lib/apollo/query-and-preload'
import { useTranslation } from '@web/src/lib/intl/react'
import { LayoutGridIcon, LayoutListIcon, PlusIcon, WrenchIcon } from 'lucide-react'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { AttachedMediaInfo, VendorMarketInfo } from 'src/lib/fragments'
import type {
  VendorMarketCategoryEnum,
  VendorMarketSortEnum,
  VendorMarketsListQuery,
  VendorMarketsListQueryVariables,
} from 'types/graphql'
import { z } from 'zod'
import { vendorMarketListPageHeadFromLoaderData } from './list.seo'

const VENDOR_MARKETS_QUERY: TypedDocumentNode<VendorMarketsListQuery, VendorMarketsListQueryVariables> = gql`
  query VendorMarketsList($page: Int, $filter: VendorMarketsFilter, $sortBy: VendorMarketSortEnum) {
    vendorMarketsPage(page: $page, filter: $filter, sortBy: $sortBy) {
      vendorMarkets {
        ...VendorMarketInfo
        firstMedia {
          ...AttachedMediaInfo
        }
      }
      count
      perPage
    }
  }
  ${VendorMarketInfo}
  ${AttachedMediaInfo}
`

type VmListLoaderDeps = {
  page: number
  name?: string
  distance: number
  categories?: string[]
  location?: string
  locationLat?: number
  locationLong?: number
  sortBy?: string
  myVendors: boolean
  needsReview: boolean
}

function vendorMarketsQueryVariablesFromDeps(deps: VmListLoaderDeps): VendorMarketsListQueryVariables {
  return {
    page: deps.page,
    filter: {
      name: deps.name,
      distance: deps.distance,
      categories: deps.categories as VendorMarketCategoryEnum[],
      location: deps.location,
      locationLat: deps.locationLat,
      locationLong: deps.locationLong,
      myVendors: deps.myVendors,
      needsReview: deps.needsReview,
    },
    sortBy: deps.sortBy as VendorMarketSortEnum,
  }
}

/** Matches `list.seo` — filters treat this as “no radius filter”. */
const DEFAULT_VM_LIST_DISTANCE = 250

type VmListSearchInput = {
  page?: number
  name?: string
  distance?: number
  categories?: string[]
  location?: string
  locationLat?: number
  locationLong?: number
  sortBy?: string
  myVendors?: boolean
  needsReview?: boolean
}

function resolveVmListSearchParams(search: VmListSearchInput): VmListLoaderDeps {
  return {
    page: search.page ?? 1,
    name: search.name,
    distance: search.distance ?? DEFAULT_VM_LIST_DISTANCE,
    categories: search.categories,
    location: search.location,
    locationLat: search.locationLat,
    locationLong: search.locationLong,
    sortBy: search.sortBy,
    myVendors: search.myVendors ?? false,
    needsReview: search.needsReview ?? false,
  }
}

export const Route = createFileRoute('/(auth)/(main-layout)/vendor-market/list')({
  component: VmListPage,
  staticData: {
    hideFooter: true,
  },
  validateSearch: z.object({
    page: z.coerce.number().optional(),
    name: z.string().optional(),
    distance: z.coerce.number().optional(),
    categories: z.array(z.string()).optional(),
    location: z.string().optional(),
    locationLat: z.coerce.number().optional(),
    locationLong: z.coerce.number().optional(),
    sortBy: z.string().optional(),
    myVendors: z.coerce.boolean().optional(),
    needsReview: z.coerce.boolean().optional(),
  }),
  search: {
    middlewares: [
      stripSearchParams({
        page: 1,
        distance: DEFAULT_VM_LIST_DISTANCE,
        myVendors: false,
        needsReview: false,
        sortBy: 'DISTANCE',
        categories: [],
        location: '',
        locationLat: 0,
        locationLong: 0,
      }),
    ],
  },
  head: ({ loaderData, match }) => vendorMarketListPageHeadFromLoaderData(loaderData, match.search),
  loaderDeps: ({ search }) => resolveVmListSearchParams(search),
  loader: async ({ context, deps: d }) => {
    const deps = d as VmListLoaderDeps
    const { queryData, queryRef } = await queryAndPreload(context, VENDOR_MARKETS_QUERY, {
      variables: vendorMarketsQueryVariablesFromDeps(deps),
    })
    const vendorMarkets = queryData?.vendorMarketsPage?.vendorMarkets ?? []
    return {
      vendorMarketsQueryRef: queryRef,
      vendorMarketsQueryData: queryData,
      seoList: {
        items: vendorMarkets.slice(0, 24).map((vm) => ({
          name: vm.name?.trim() || 'Vendor',
          path: `/vendor-market/${vm.slug ?? vm.id}`,
          imageUrl: vm.firstMedia?.imageUrlMedium,
        })),
        totalCount: queryData?.vendorMarketsPage?.count ?? 0,
      },
    }
  },
})

function CreateSPButton() {
  const { t } = useTranslation()
  const navigate = useNavigate()

  const handleClick = () => {
    navigate({ to: '/vendor-market/create' })
  }

  return (
    <Tooltip>
      <TooltipTrigger
        render={<Button aria-label={t('Create a new vendor')} onClick={handleClick} size="icon" type="button" />}
      >
        <PlusIcon />
      </TooltipTrigger>
      <TooltipContent>{t('Create a new vendor')}</TooltipContent>
    </Tooltip>
  )
}

function LayoutToggleButton({ layout, onToggle }: { layout: VmCardLayoutMode; onToggle: () => void }) {
  const { t } = useTranslation()
  return (
    <Tooltip>
      <TooltipTrigger
        render={
          <Button
            aria-label={
              layout === 'compact' ? t('Switch to stacked vendor cards') : t('Switch to compact vendor cards')
            }
            onClick={onToggle}
            size="icon"
            type="button"
            variant="secondary"
          />
        }
      >
        {layout !== 'compact' ? <LayoutGridIcon className="size-4" /> : <LayoutListIcon className="size-4" />}
      </TooltipTrigger>
      <TooltipContent side="bottom">
        {layout === 'compact' ? t('Show image above details') : t('Show image beside details')}
      </TooltipContent>
    </Tooltip>
  )
}

function VmListPage() {
  const { t } = useTranslation()
  const loaderData = Route.useLoaderData()
  const search = useSearch({ strict: false })
  const resolvedSearch = useMemo(() => resolveVmListSearchParams(search), [search])
  const { data, error } = useReadQuery(loaderData.vendorMarketsQueryRef as QueryRef<VendorMarketsListQuery>)

  const searchRef = useRef(search)
  searchRef.current = search
  const navigate = useNavigate()
  const { currentUser, hasPermission, isAuthenticated, loading: authLoading } = useAuth()
  const isRoutePending = useRouterState({ select: (s) => s.status === 'pending' })
  const [filterNavigatePending, setFilterNavigatePending] = useState(false)

  useEffect(() => {
    if (!isRoutePending) {
      setFilterNavigatePending(false)
    }
  }, [isRoutePending])

  useEffect(() => {
    if (authLoading) {
      return
    }
    if (!isAuthenticated && resolvedSearch.page > 2) {
      navigate({ to: '/auth/signup', replace: true } as never)
    }
  }, [authLoading, isAuthenticated, navigate, resolvedSearch.page])

  const isFilterLoading = filterNavigatePending && isRoutePending

  const [vmCardLayout, setVmCardLayout] = useState<VmCardLayoutMode>('regular')

  const resolvedVendorMarketsQuery = data ?? loaderData.vendorMarketsQueryData
  const resolvedPage =
    resolvedVendorMarketsQuery?.vendorMarketsPage ??
    ({
      vendorMarkets: [],
      count: 0,
      perPage: 1,
    } as NonNullable<VendorMarketsListQuery['vendorMarketsPage']>)

  const listFilterDefaults = useMemo(
    (): VmListFilterFormValues =>
      vmListFilterFormValuesFromResolvedParams({
        name: search.name,
        distance: resolvedSearch.distance,
        categories: search.categories,
        location: search.location ?? currentUser?.profile?.location,
        locationLat: search.locationLat ?? currentUser?.profile?.locationLat,
        locationLong: search.locationLong ?? currentUser?.profile?.locationLong,
        sortBy: search.sortBy,
        myVendors: resolvedSearch.myVendors,
        needsReview: resolvedSearch.needsReview,
      }),
    [
      search.name,
      resolvedSearch.distance,
      search.categories,
      search.location,
      search.locationLat,
      search.locationLong,
      resolvedSearch.myVendors,
      resolvedSearch.needsReview,
      search.sortBy,
      currentUser?.profile?.location,
      currentUser?.profile?.locationLat,
      currentUser?.profile?.locationLong,
    ]
  )

  const listFilterResetValues = useMemo(
    (): VmListFilterFormValues =>
      vmListFilterFormValuesFromResolvedParams({
        name: undefined,
        distance: 250,
        categories: undefined,
        location: currentUser?.profile?.location,
        locationLat: currentUser?.profile?.locationLat,
        locationLong: currentUser?.profile?.locationLong,
        sortBy: undefined,
        myVendors: false,
        needsReview: false,
      }),
    [currentUser?.profile?.location, currentUser?.profile?.locationLat, currentUser?.profile?.locationLong]
  )

  const sortFormMethods = useForm<{ sortBy?: string }>({
    defaultValues: { sortBy: search.sortBy },
  })

  useEffect(() => {
    sortFormMethods.reset({ sortBy: search.sortBy })
  }, [search.sortBy, sortFormMethods])

  const applyListFilters = useCallback(
    (values: VmListFilterFormValues, options: { includeSort: boolean }) => {
      setFilterNavigatePending(true)
      navigate({
        search: {
          ...searchRef.current,
          page: 1,
          name: values.name || undefined,
          distance: values.distance?.[0] < 1000 ? values.distance[0] : 1000,
          categories: values.categories?.length > 0 ? values.categories : undefined,
          location: values.location?.name || undefined,
          locationLat: values.location?.latitude || undefined,
          locationLong: values.location?.longitude || undefined,
          myVendors: values.myVendors || undefined,
          needsReview: values.needsReview || undefined,
          ...(options.includeSort ? { sortBy: values.sortBy || undefined } : {}),
        },
        replace: true,
      })
    },
    [navigate]
  )

  const handleSortChange = useCallback(() => {
    const sortBy = sortFormMethods.getValues('sortBy')
    navigate({
      search: {
        ...searchRef.current,
        page: 1,
        sortBy: sortBy || undefined,
      },
      replace: true,
    })
  }, [navigate, sortFormMethods])

  const handlePageChange = useCallback(
    (nextPage: number): void => {
      if (!isAuthenticated && nextPage > 2) {
        navigate({ to: '/auth/signup' } as never)
        return
      }
      navigate({ search: { ...searchRef.current, page: nextPage }, replace: true })
    },
    [isAuthenticated, navigate]
  )

  const handleReset = useCallback(() => {
    navigate({ to: '/vendor-market/list', replace: true } as Parameters<typeof navigate>[0])
  }, [navigate])

  const getPagePreTitle = useCallback(() => {
    if (resolvedSearch.myVendors) return t('My')
    return t('Discover')
  }, [resolvedSearch.myVendors, t])

  const getPageSubtitle = useCallback(() => {
    if (resolvedSearch.myVendors) return t('Browse vendors you have added or maintain.')
    return t('Browse vendors offering trades and services — filter by distance, category, and what you need.')
  }, [resolvedSearch.myVendors, t])

  if (error) {
    return <ErrorAlert error={error} />
  }

  return (
    <div className="flex w-full flex-col md:flex-row">
      <aside className="hidden w-fit shrink-0 overflow-visible md:sticky md:top-14 md:flex md:h-[calc(100dvh-3.5rem)] md:flex-col md:self-start">
        <VmListSideNav
          defaultLocation={search.location ?? currentUser?.profile?.location}
          filterDefaults={listFilterDefaults}
          filterResetValues={listFilterResetValues}
          hasPermission={hasPermission}
          isAuthenticated={isAuthenticated}
          onFiltersApply={(values) => applyListFilters(values, { includeSort: false })}
          onReset={handleReset}
        />
      </aside>

      <div className="flex w-full min-w-0 flex-1 flex-col">
        <RouteLoadingIndicator loading={isFilterLoading} size="xs" variant="progress" />
        <div className="w-full max-w-full px-4 py-2 md:px-8 lg:px-16">
          <PageHeader
            breadcrumbItems={[
              { label: t('Home'), to: '/' },
              { label: t('Vendor Market'), to: '/vendor-market' },
              { label: t('Discover Vendors') },
            ]}
            className="mb-0 items-stretch md:mb-4"
            highlightTitle={t('Vendors')}
            preTitle={getPagePreTitle()}
            rightChild={
              <>
                <div className="flex flex-col items-end gap-3 md:hidden">
                  {isAuthenticated ? <CreateSPButton /> : null}
                </div>
                <div className="hidden h-full flex-col items-end gap-3 md:flex">
                  {isAuthenticated ? (
                    <div className="flex gap-2">
                      <CreateSPButton />
                    </div>
                  ) : null}

                  <div className="mt-auto">
                    <div className="flex items-center gap-2">
                      <span className="shrink-0 text-nowrap font-medium text-muted-foreground text-sm">
                        {t('Sort By:')}
                      </span>
                      <Form formMethods={sortFormMethods} suppressHydrationWarning>
                        <NativeSelectFormField name="sortBy" onValueChange={handleSortChange}>
                          <option value="DISTANCE">{t('Distance')}</option>
                          <option value="LATEST">{t('Latest')}</option>
                          <option value="NAME">{t('Name')}</option>
                          <option value="RATING">{t('Rating')}</option>
                        </NativeSelectFormField>
                      </Form>
                      <LayoutToggleButton
                        layout={vmCardLayout}
                        onToggle={() => setVmCardLayout((prev) => (prev === 'compact' ? 'regular' : 'compact'))}
                      />
                    </div>
                  </div>
                </div>
              </>
            }
            subtitle={getPageSubtitle()}
          />

          <div className="my-4">
            <VmMenu activeTab="discover" />
          </div>

          <div className="mb-6 w-full md:hidden">
            <VmListTopNav
              defaultLocation={search.location ?? currentUser?.profile?.location}
              filterDefaults={listFilterDefaults}
              filterResetValues={listFilterResetValues}
              hasPermission={hasPermission}
              isAuthenticated={isAuthenticated}
              onFiltersApply={(values) => applyListFilters(values, { includeSort: true })}
              onReset={handleReset}
              rightBarChildren={
                <>
                  {isAuthenticated && <CreateSPButton />}
                  <LayoutToggleButton
                    layout={vmCardLayout}
                    onToggle={() => setVmCardLayout((prev) => (prev === 'compact' ? 'regular' : 'compact'))}
                  />
                </>
              }
            />
          </div>

          {resolvedPage.vendorMarkets.length > 0 ? (
            <div className="flex w-full flex-col gap-0">
              <div
                className={
                  vmCardLayout === 'compact'
                    ? 'grid w-full grid-cols-1 items-stretch gap-4 md:gap-6'
                    : 'grid w-full grid-cols-1 items-stretch gap-4 md:grid-cols-2 md:gap-6 lg:grid-cols-3 xl:grid-cols-4'
                }
              >
                {resolvedPage.vendorMarkets?.map((sp) => (
                  <div className="flex h-full w-full min-w-0 flex-col" key={sp.id}>
                    <VmCard cardLayout={vmCardLayout} vendor={sp} />
                  </div>
                ))}
              </div>

              <PaginationControl
                count={resolvedPage.count ? resolvedPage.count : 1}
                onPageChange={(e) => handlePageChange(e.page)}
                page={resolvedSearch.page}
                pageSize={resolvedPage.perPage ? resolvedPage.perPage : 1}
              />
            </div>
          ) : (
            <Empty className="w-full pb-20">
              <EmptyHeader>
                <EmptyMedia variant="icon">
                  <WrenchIcon className="size-8" />
                </EmptyMedia>
                <EmptyTitle>{t('No vendors found')}</EmptyTitle>
                <EmptyDescription>{t('Try adjusting your search or filters.')}</EmptyDescription>
              </EmptyHeader>
            </Empty>
          )}
        </div>

        <Footer />
      </div>
    </div>
  )
}
